import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Snackbar, 
  Alert, 
  Slide, 
  Box, 
  Typography, 
  Chip,
  useTheme,
  useMediaQuery 
} from '@mui/material';
import { 
  Wifi as WifiIcon, 
  CloudDone as CloudIcon,
  Sync as SyncIcon 
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import useNetworkStatus from '../../../hooks/useNetworkStatus'; // ✅ FIXED: Correct path

// ✅ FIXED: Create motion components properly
const MotionDiv = motion.div;
const MotionBox = motion.div; // ✅ FIXED: Use motion.div instead of motion(Box)

const OnlineStatusIndicator = () => {
  const { isOnline, wasOffline } = useNetworkStatus();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [showSyncing, setShowSyncing] = useState(false);

  // Only show in dashboard routes
  const isDashboard = location.pathname.startsWith('/dashboard');

  useEffect(() => {
    if (isDashboard && isOnline && wasOffline) {
      // Show "back online" message
      setShowBackOnline(true);
      
      // Show syncing after a short delay
      setTimeout(() => {
        setShowBackOnline(false);
        setShowSyncing(true);
        
        // Hide syncing after 3 seconds
        setTimeout(() => {
          setShowSyncing(false);
        }, 3000);
      }, 2000);
    }
  }, [isOnline, wasOffline, isDashboard]);

  if (!isDashboard) return null;

  return (
    <>
      {/* ✅ Subtle Corner Indicator - Always Visible */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 70, md: 80 }, // Below header
          right: { xs: 16, md: 24 },
          zIndex: 1200,
          pointerEvents: 'none'
        }}
      >
        <AnimatePresence>
          <MotionDiv
            initial={{ opacity: 0, scale: 0.8, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Chip
              icon={isOnline ? <WifiIcon /> : <WifiIcon />}
              label={isOnline ? 'Online' : 'Offline'}
              size={isMobile ? 'small' : 'medium'} // ✅ FIXED: Use isMobile
              sx={{
                background: isOnline 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: isMobile ? '0.65rem' : '0.7rem', // ✅ FIXED: Use isMobile
                height: isMobile ? 20 : 24, // ✅ FIXED: Use isMobile
                boxShadow: isOnline 
                  ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                  : '0 4px 12px rgba(239, 68, 68, 0.3)',
                border: `1px solid ${isOnline ? '#10b98120' : '#ef444420'}`,
                backdropFilter: 'blur(10px)',
                '& .MuiChip-icon': {
                  fontSize: isMobile ? 12 : 14, // ✅ FIXED: Use isMobile
                  color: 'white'
                }
              }}
            />
          </MotionDiv>
        </AnimatePresence>
      </Box>

      {/* ✅ Back Online Notification */}
      <Snackbar
        open={showBackOnline}
        autoHideDuration={2000}
        onClose={() => setShowBackOnline(false)}
        anchorOrigin={{ 
          vertical: 'top', 
          horizontal: 'center'
        }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'down' }}
        sx={{
          mt: { xs: 8, md: 10 }, // Below header
          '& .MuiSnackbar-root': {
            position: 'relative'
          }
        }}
      >
        <Alert
          severity="success"
          icon={<CloudIcon />}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            fontWeight: 600,
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
            fontSize: isMobile ? '0.8rem' : '0.875rem', // ✅ FIXED: Use isMobile
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            🎉 Back online! All systems connected
          </Typography>
        </Alert>
      </Snackbar>

      {/* ✅ Syncing Data Notification */}
      <Snackbar
        open={showSyncing}
        autoHideDuration={3000}
        onClose={() => setShowSyncing(false)}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: isMobile ? 'center' : 'right' // ✅ FIXED: Use isMobile
        }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: isMobile ? 'up' : 'left' }} // ✅ FIXED: Use isMobile
      >
        <Alert
          severity="info"
          icon={
            <MotionDiv
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <SyncIcon />
            </MotionDiv>
          }
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            fontWeight: 600,
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
            fontSize: isMobile ? '0.8rem' : '0.875rem', // ✅ FIXED: Use isMobile
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              📡 Syncing your data...
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              All changes are being synchronized
            </Typography>
          </MotionBox>
        </Alert>
      </Snackbar>
    </>
  );
};

export default OnlineStatusIndicator;